import { Link, Navigate, useLocation } from 'react-router-dom';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { isAppPath } from '../lib/appPaths';
import { isPlatformPath } from '../lib/crumbs';
import { usePortal } from '../lib/portal';
import '../styles/landing.css';
import '../styles/public.css';

/**
 * A page for an address that does not exist.
 *
 * It replaces a silent redirect to "/". The redirect was worse than it looks:
 * a mistyped or dead link dropped you on the homepage with no explanation, so
 * a visitor could not tell whether the page had moved, whether they had
 * mistyped, or whether the site had simply lost their request. Saying so, and
 * offering the way out, is both shorter and honest.
 *
 * The exits are chosen from who is asking. Someone signed in is far more
 * likely to want the place they were working than the marketing homepage, and
 * a platform operator wants the Control Center — so the primary action is the
 * one that fits, and the others stay available underneath.
 */
export function NotFound() {
  const { isAuthenticated, platform: staff, siteHome } = usePortal();
  const { pathname, search } = useLocation();
  // A real screen reached with no session (it ended while the page was open, or
  // the link was opened signed out): sign in, then come back to it.
  if (!isAuthenticated && isAppPath(pathname)) {
    return <Navigate to="/login" replace state={{ from: pathname + search }} />;
  }
  // A staff member who mistyped a Control Center address wants the Control
  // Center back, not the landlord dashboard they do not have.
  const platform = staff && isPlatformPath(pathname);

  return (
    <div className="lp mk">
      <PublicHeader />

      <div className="mk-success">
        <div className="mk-success-mark" aria-hidden
          style={{ background: 'var(--brand-100)', color: 'var(--brand-700)' }}>?</div>
        <h1 className="mk-h2">We could not find that page</h1>
        <p className="mk-p">
          The address <code>{pathname}</code> does not exist on NyumbaSuite. It may have
          moved, or the link that brought you here may be out of date.
        </p>

        <div className="mk-cta-row" style={{ marginTop: 'var(--space-6)' }}>
          {platform && <Link className="btn btn-primary" to="/admin">Back to Control Center</Link>}
          {isAuthenticated && !platform && <Link className="btn btn-primary" to="/">Back to my dashboard</Link>}
          <Link className={`btn ${isAuthenticated ? 'btn-secondary' : 'btn-primary'}`} to={siteHome}>
            Go to the homepage
          </Link>
          {!isAuthenticated && <Link className="btn btn-secondary" to="/login">Sign in</Link>}
        </div>

        <p className="mk-p" style={{ marginTop: 'var(--space-6)' }}>
          Or try <Link to="/homes">Find your next home</Link>, the{' '}
          <Link to="/blog">blog</Link>, or <Link to="/about">about us</Link>.
        </p>
      </div>

      <SiteFooter />
    </div>
  );
}
